import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';
import { toast } from 'sonner';
import {
  MessageCircle,
  Flag,
  Search,
  SlidersHorizontal,
  Clock,
  User,
  Heart,
  ThumbsDown
} from 'lucide-react';

// Comment interface
interface Comment {
  id: number;
  content: string;
  author: {
    id: number;
    name: string;
    avatar?: string;
  };
  stream?: {
    id: number;
    title: string;
  };
  likes: number;
  dislikes: number;
  createdAt: string;
  status: 'pending' | 'approved' | 'rejected';
  userReaction?: 'like' | 'dislike' | null;
}

interface CommentsResponse {
  comments: Comment[];
  total: number;
  page: number;
  pageSize: number;
}

export default function CommentsPage() {
  const { user } = useAuthStore();
  const isModeratorOrAdmin = user?.role?.name === 'admin' || user?.role?.name === 'moderator';
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const pageSize = 20;
  const sentinelRef = useRef<HTMLDivElement>(null);
  const fetchingRef = useRef(false);

  const hasMore = total > 0 && comments.length < total;

  const fetchComments = useCallback(async (offset: number, append: boolean) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);
      setError(null);

      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: offset.toString(),
        ...(statusFilter !== 'all' && { status: statusFilter }),
      });

      const endpoint = isModeratorOrAdmin
        ? `/api/admin/comments?${params.toString()}`
        : `/api/comments/me?${params.toString()}`;

      const response = await api.get<CommentsResponse>(endpoint);

      if (response.success && response.data) {
        const newComments = response.data.comments ?? [];
        if (!append) setComments(newComments);
        else setComments(prev => [...prev, ...newComments]);
        setTotal(response.data.total ?? 0);
      } else {
        setError(response.error?.message || 'Failed to load comments');
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [statusFilter, isModeratorOrAdmin]);

  // Reset and reload when filters change
  useEffect(() => {
    setComments([]);
    setTotal(0);
    fetchComments(0, false);
  }, [fetchComments]);

  // Infinite scroll: observe sentinel at bottom of list
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setComments(current => {
            const currentHasMore = total > 0 && current.length < total;
            if (currentHasMore && !fetchingRef.current) {
              fetchComments(current.length, true);
            }
            return current;
          });
        }
      },
      { rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [total, fetchComments]);

  const handleReaction = async (commentId: number, reaction: 'like' | 'dislike') => {
    try {
      const currentComment = comments.find((comment) => comment.id === commentId);
      const nextReaction = currentComment?.userReaction === reaction ? null : reaction;

      const response = await api.post<{
        reaction: 'like' | 'dislike' | null;
        likeCount: number;
        dislikeCount: number;
      }>(`/api/comments/${commentId}/reaction`, {
        reaction: nextReaction,
      });

      if (response.success && response.data) {
        const reactionData = response.data;
        // Update local state
        setComments(prev => prev.map(comment =>
          comment.id === commentId
            ? {
                ...comment,
                likes: reactionData.likeCount,
                dislikes: reactionData.dislikeCount,
                userReaction: reactionData.reaction
              }
            : comment
        ));
      } else {
        toast.error(response.error?.message || 'Failed to save reaction');
      }
    } catch (err) {
      console.error('Error reacting to comment:', err);
      toast.error('Failed to save reaction');
    }
  };

  const handleFlag = async (commentId: number) => {
    try {
      const response = await api.post(`/api/comments/${commentId}/report`, { reason: 'flagged' });
      if (response.success) {
        toast.success('Comment flagged for review');
      } else {
        toast.error(response.error?.message || 'Failed to flag comment');
      }
    } catch {
      toast.error('Failed to flag comment');
    }
  };

  const handleModerate = async (commentId: number, action: 'approve' | 'reject') => {
    try {
      const response = await api.put(`/api/comments/${commentId}/moderate`, { action });

      if (response.success) {
        setComments(prev => prev.map(comment =>
          comment.id === commentId
            ? { ...comment, status: action === 'approve' ? 'approved' : 'rejected' }
            : comment
        ));
        toast.success(action === 'approve' ? 'Comment approved' : 'Comment rejected');
      } else {
        toast.error(response.error?.message || `Failed to ${action} comment`);
      }
    } catch (err) {
      console.error('Error moderating comment:', err);
      toast.error(`Failed to ${action} comment`);
    }
  };

  const filteredComments = comments.filter(comment =>
    comment.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    comment.author.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (comment.stream?.title.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'approved':
        return <Badge variant="default">Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && comments.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="@container/comments max-w-[1800px] w-full mx-auto px-3 py-4 @[740px]/comments:px-4 @[740px]/comments:py-5 @[1100px]/comments:px-6 @[1100px]/comments:py-6 space-y-4 @[740px]/comments:space-y-5 @[1100px]/comments:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Comments</h1>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-3">
            <Sheet open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm">
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[min(92vw,420px)] p-0">
                <SheetHeader className="border-b p-4 pr-12">
                  <SheetTitle>Comment Filters</SheetTitle>
                  <SheetDescription>Filter comments by search text and moderation status.</SheetDescription>
                </SheetHeader>
                <div className="space-y-4 p-4">
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Search</p>
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search comments, authors, or streams..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Status</p>
                    <Tabs value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="all">All</TabsTrigger>
                        <TabsTrigger value="pending">Pending</TabsTrigger>
                        <TabsTrigger value="approved">Approved</TabsTrigger>
                        <TabsTrigger value="rejected">Rejected</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            {(searchTerm.trim() || statusFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Comments List */}
      <div className="space-y-4">
        {filteredComments.map((comment) => (
          <Card key={comment.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3">
                  {/* Author and Meta */}
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={comment.author.avatar} />
                      <AvatarFallback>
                        {comment.author.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{comment.author.name}</span>
                        {getStatusBadge(comment.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(comment.createdAt)}
                        </span>
                        {comment.stream && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {comment.stream.title}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Comment Content */}
                  <p className="text-sm leading-relaxed">{comment.content}</p>

                  {/* Actions */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReaction(comment.id, 'like')}
                        className={`h-8 px-2 ${comment.userReaction === 'like' ? 'text-green-600' : ''}`}
                      >
                        <Heart className="h-3 w-3 mr-1" />
                        {comment.likes}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReaction(comment.id, 'dislike')}
                        className={`h-8 px-2 ${comment.userReaction === 'dislike' ? 'text-red-600' : ''}`}
                      >
                        <ThumbsDown className="h-3 w-3 mr-1" />
                        {comment.dislikes}
                      </Button>
                    </div>

                    {/* Moderation Actions (for moderators/admins) */}
                    {isModeratorOrAdmin ? (
                      <div className="flex items-center gap-2">
                        {comment.status === 'pending' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModerate(comment.id, 'approve')}
                              className="h-8"
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModerate(comment.id, 'reject')}
                              className="h-8"
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          title="Flag for review"
                          onClick={() => handleFlag(comment.id)}
                        >
                          <Flag className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredComments.length === 0 && !loading && !error && (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No comments found</h3>
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Comments will appear here once users start commenting on streams'
                }
              </p>
            </CardContent>
          </Card>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-1" />

        {/* Loading more indicator */}
        {loadingMore && (
          <div className="flex justify-center py-6">
            <LoadingSpinner size="sm" />
          </div>
        )}
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4">
            <p className="text-destructive text-center">{error}</p>
            <Button
              variant="outline"
              onClick={() => fetchComments(0, false)}
              className="mt-2 w-full"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
